import { css, fillOutlined, circle, shade } from './draw-utils';
import type { ProceduralDef } from '../types';

/**
 * Shared helpers for the sector theme kits (docs/04 environment themes):
 * deterministic per-asset randomness, tile wrapping and the factories every
 * theme uses (floor/wall tiles, edge blobs, cover rocks, decor sheets).
 */

/** Deterministic [0,1) stream for drawing (never Math.random). */
export function prand(seed: number): () => number {
  let s = Math.imul(seed, 2654435761) >>> 0 || 1; // exact 32-bit mix (no float overflow)
  return () => {
    s ^= s << 13;
    s >>>= 0;
    s ^= s >>> 17;
    s ^= s << 5;
    s >>>= 0;
    return s / 4294967296;
  };
}

/** Draw `fn` at the 9 wrap offsets so shapes crossing an edge tile seamlessly. */
export function wrap(w: number, h: number, fn: (dx: number, dy: number) => void): void {
  for (const dx of [-w, 0, w]) for (const dy of [-h, 0, h]) fn(dx, dy);
}

/** Lumpy closed polygon around (cx, cy). */
export function lumpPath(
  ctx: CanvasRenderingContext2D, cx: number, cy: number, r: number,
  lumps: number, wobble: number, phase: number,
): void {
  ctx.beginPath();
  for (let i = 0; i <= lumps; i++) {
    const a = (Math.PI * 2 * i) / lumps + phase;
    const rr = r * (1 - wobble + wobble * Math.sin(i * 2.3 + phase * 3));
    const x = cx + Math.cos(a) * rr;
    const y = cy + Math.sin(a) * rr;
    if (i === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  }
  ctx.closePath();
}

/**
 * Tileable floor / wall-mass texture: base colour + themed detail. Floors
 * use 512² (repeats are less visible over big fields), wall mass 256².
 */
export function makeTile(
  key: string, base: number, seed: number,
  detail: (ctx: CanvasRenderingContext2D, w: number, h: number, rnd: () => number) => void,
  size = 256,
): ProceduralDef {
  return {
    key, width: size, height: size, anchorX: 0, anchorY: 0, frames: 1,
    draw(ctx, w, h) {
      ctx.fillStyle = css(base);
      ctx.fillRect(0, 0, w, h);
      detail(ctx, w, h, prand(seed));
    },
  };
}

/** Soft tone blotches that break up visible tiling. */
export function blotches(
  ctx: CanvasRenderingContext2D, w: number, h: number, rnd: () => number,
  color: number, alpha: number, count: number, rMin: number, rMax: number,
): void {
  ctx.fillStyle = css(color);
  ctx.globalAlpha = alpha;
  for (let i = 0; i < count; i++) {
    const x = rnd() * w;
    const y = rnd() * h;
    const r = rMin + rnd() * (rMax - rMin);
    wrap(w, h, (dx, dy) => {
      circle(ctx, x + dx, y + dy, r);
      ctx.fill();
    });
  }
  ctx.globalAlpha = 1;
}

/** Edge wall blob (3 variants): outlined lump + top-left light + accent fleck. */
export function makeWallBlob(key: string, base: number, light: number, accent: number): ProceduralDef {
  return {
    key, width: 128, height: 128, anchorX: 0.5, anchorY: 0.5, frames: 3,
    draw(ctx, w, _h, frame) {
      const c = w / 2;
      const r = [w * 0.42, w * 0.38, w * 0.45][frame]!;
      lumpPath(ctx, c, c, r, 9, 0.08, frame * 0.9);
      fillOutlined(ctx, base, 7);
      ctx.save();
      lumpPath(ctx, c, c, r, 9, 0.08, frame * 0.9);
      ctx.clip();
      circle(ctx, c - r * 0.32, c - r * 0.32, r * 0.72);
      ctx.fillStyle = css(light);
      ctx.fill();
      ctx.fillStyle = css(accent);
      circle(ctx, c + r * 0.3, c + r * 0.22, r * 0.12);
      ctx.fill();
      ctx.restore();
    },
  };
}

export type RockStyle = 'chunk' | 'lump' | 'shard';

/** Cover rock piece (3 variants) — chunky scrap, round flesh or crystal shard. */
export function makeRock(
  key: string, style: RockStyle, base: number, light: number, accent: number,
): ProceduralDef {
  return {
    key, width: 128, height: 128, anchorX: 0.5, anchorY: 0.5, frames: 3,
    draw(ctx, w, _h, frame) {
      const c = w / 2;
      const r = [w * 0.4, w * 0.36, w * 0.43][frame]!;
      if (style === 'shard') {
        drawShard(ctx, c, c, r, frame, base, light, accent);
        return;
      }
      const lumps = style === 'chunk' ? 7 : 12;
      const wob = style === 'chunk' ? 0.16 : 0.07;
      lumpPath(ctx, c, c, r, lumps, wob, frame * 0.7);
      fillOutlined(ctx, base, 7);
      circle(ctx, c - r * 0.25, c - r * 0.25, r * 0.45);
      ctx.fillStyle = css(light);
      ctx.fill();
      ctx.strokeStyle = css(accent);
      ctx.lineWidth = 3;
      ctx.lineCap = 'round';
      ctx.beginPath();
      if (style === 'chunk') {
        // Plate seam + bolt
        ctx.moveTo(c - r * 0.5, c + r * 0.15);
        ctx.lineTo(c + r * 0.45, c + r * 0.05);
        ctx.stroke();
        circle(ctx, c + r * 0.25, c + r * 0.38, 3.5);
        ctx.fillStyle = css(accent);
        ctx.fill();
      } else {
        // Pores
        for (let i = 0; i < 3; i++) {
          circle(ctx, c + (i - 1) * r * 0.32, c + r * 0.32 - (i % 2) * r * 0.2, 4 - i * 0.6);
          ctx.fillStyle = css(accent);
          ctx.fill();
        }
      }
    },
  };
}

function drawShard(
  ctx: CanvasRenderingContext2D, cx: number, cy: number, r: number, frame: number,
  base: number, light: number, accent: number,
): void {
  const n = 5 + frame;
  const pts: Array<[number, number]> = [];
  for (let i = 0; i < n; i++) {
    const a = (Math.PI * 2 * i) / n + frame * 0.5;
    const rr = r * (i % 2 === 0 ? 1 : 0.72);
    pts.push([cx + Math.cos(a) * rr, cy + Math.sin(a) * rr]);
  }
  ctx.beginPath();
  pts.forEach(([x, y], i) => (i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y)));
  ctx.closePath();
  fillOutlined(ctx, base, 6);
  // Lit facets toward the top-left
  for (let i = 0; i < n; i++) {
    const [x0, y0] = pts[i]!;
    const [x1, y1] = pts[(i + 1) % n]!;
    if ((x0 + x1) / 2 > cx || (y0 + y1) / 2 > cy) continue;
    ctx.beginPath();
    ctx.moveTo(cx, cy);
    ctx.lineTo(x0, y0);
    ctx.lineTo(x1, y1);
    ctx.closePath();
    ctx.fillStyle = css(light);
    ctx.fill();
  }
  circle(ctx, cx, cy, r * 0.16);
  ctx.fillStyle = css(accent);
  ctx.fill();
  ctx.strokeStyle = css(shade(base, 0.7));
  ctx.lineWidth = 2;
  for (const [x, y] of pts) {
    ctx.beginPath();
    ctx.moveTo(cx, cy);
    ctx.lineTo(x, y);
    ctx.stroke();
  }
}

/** Multi-variant decor sheet (decals / props): one frame per variant. */
export function makeSheet(
  key: string, size: number, frames: number,
  drawFrame: (ctx: CanvasRenderingContext2D, c: number, frame: number, rnd: () => number) => void,
): ProceduralDef {
  return {
    key, width: size, height: size, anchorX: 0.5, anchorY: 0.5, frames,
    draw(ctx, w, _h, frame) {
      drawFrame(ctx, w / 2, frame, prand(frame * 97 + key.length * 13));
    },
  };
}

/** Radial glow disc (alpha falloff) — lamps, cores, spores. */
export function glow(
  ctx: CanvasRenderingContext2D, x: number, y: number, r: number, rgb: string, alpha = 1,
): void {
  const g = ctx.createRadialGradient(x, y, 0, x, y, r);
  g.addColorStop(0, `rgba(${rgb}, ${alpha})`);
  g.addColorStop(1, `rgba(${rgb}, 0)`);
  ctx.fillStyle = g;
  circle(ctx, x, y, r);
  ctx.fill();
}
